<script lang="ts">
import { defineComponent, ref, watch, computed, type PropType } from "vue";
import type { UIPendingKoDiscardChoice } from "@legendary-arena/game-engine";
import type { SubmitMove } from "./uiMoveName.types";

/**
 * Inline prompt for resolving a pending KO-from-discard choice (WP-693 / D-24510 —
 * Loki's Maniacal Tyrant: "KO up to four cards from your discard pile"; WP-760 /
 * D-24589 — The Fallen's Salomé: "KO up to two cards from your discard pile").
 *
 * Renders iff `pendingKoDiscardChoice !== undefined AND viewerPlayerId === playerID`.
 * Hidden for opponents and spectators. Shows the chooser's discard; the player picks
 * 0..maxCount cards to KO and submits `resolveKoDiscardChoice({ cardIds })`. An empty
 * selection ("KO None") is the legal "KO nothing" choice.
 *
 * // why: D-24682 — the selection is by discard INDEX, so two copies of a shared-id card
 * (two S.H.I.E.L.D. Agents) are two separate picks; the submitted cardIds may repeat an
 * id once per copy, which the engine accepts as a multiset.
 *
 * NOT a modal — the choice is game-blocking and cannot be dismissed.
 * NOT position:fixed. NOT <Teleport>. Renders in normal document flow.
 *
 * Mirrors PendingPutCardsOnDeckChoicePrompt.vue (mirror-not-import). Per D-6512: uses
 * `defineComponent({ setup() { return {...} } })`.
 *
 * @see WP-693 §Scope (In) — inline prompt spec
 * @see EC-730 Locked Values — move args, render formula, gate precedence
 * @see DECISIONS.md D-24510
 */
export default defineComponent({
  name: "PendingKoDiscardChoicePrompt",
  props: {
    pendingKoDiscardChoice: {
      type: Object as PropType<UIPendingKoDiscardChoice | undefined>,
      required: false,
      default: undefined,
    },
    viewerPlayerId: {
      // why: null signals a spectator with no assigned playerId; prompt
      // must not render in that case.
      type: [String, null] as unknown as PropType<string | null>,
      required: true,
    },
    submitMove: {
      type: Function as PropType<SubmitMove>,
      required: true,
    },
  },
  setup(props) {
    // why: isSubmitting debounces a same-frame double-click ONLY; selectedIndices holds
    // the in-progress selection (order irrelevant — KO is a set). BOTH must reset on
    // every new server frame — the parent page keeps this component mounted for the whole
    // match (only its inner `v-if` content toggles), so a persistent latch would freeze
    // the next choice. Each server frame delivers a fresh pendingKoDiscardChoice object;
    // resetting on its identity change re-enables the prompt and recovers from a no-op
    // resubmit. Without this the board stays frozen by the block-all guard.
    const isSubmitting = ref(false);
    const selectedIndices = ref<number[]>([]);
    watch(
      () => props.pendingKoDiscardChoice,
      () => {
        isSubmitting.value = false;
        selectedIndices.value = [];
      },
    );

    function shouldRender(): boolean {
      return (
        props.pendingKoDiscardChoice !== undefined &&
        props.viewerPlayerId !== null &&
        props.viewerPlayerId === props.pendingKoDiscardChoice.playerID
      );
    }

    // why: the player may KO at most `maxCount` cards (the printed "up to four").
    const maxCount = computed<number>(() => {
      if (!props.pendingKoDiscardChoice) return 0;
      return props.pendingKoDiscardChoice.maxCount;
    });

    // why: D-24783 — a grey-filtered entry (Frost Giant Warrior / Ymir: "KO up to N grey
    // Heroes") lists only those cards, so the header names them; an unfiltered entry keeps
    // the source-neutral "card(s)" wording (WP-760 / D-24589).
    const headingText = computed<string>(() => {
      // why: WP-850 / D-24786 — an entry limited to the cards one play just discarded (Bishop's
      // Firepower from the Future, "KO any number of those cards") says so. N is the number of
      // cards LISTED, not the park-time maxCount: a later discard change in the same move can
      // only shrink the list.
      if (props.pendingKoDiscardChoice?.isLimitedToDiscardedThisWay === true) {
        const listedCount = props.pendingKoDiscardChoice.discard.length;
        const listedNoun = listedCount === 1 ? "card" : "cards";
        return `KO any number of the ${listedCount} ${listedNoun} you just discarded`;
      }
      const count = maxCount.value;
      const greyFilter = props.pendingKoDiscardChoice?.greyFilter;
      let noun: string;
      if (greyFilter === "grey") {
        noun = count === 1 ? "grey Hero" : "grey Heroes";
      } else if (greyFilter === "non-grey") {
        noun = count === 1 ? "non-grey Hero" : "non-grey Heroes";
      } else {
        noun = count === 1 ? "card" : "cards";
      }
      return `KO up to ${count} ${noun} from your discard pile`;
    });

    function isSelected(discardIndex: number): boolean {
      return selectedIndices.value.includes(discardIndex);
    }

    function toggleCard(discardIndex: number): void {
      if (isSubmitting.value) return;
      if (selectedIndices.value.includes(discardIndex)) {
        selectedIndices.value = selectedIndices.value.filter((index) => index !== discardIndex);
        return;
      }
      // why: never let the player over-select beyond the cap.
      if (selectedIndices.value.length >= maxCount.value) return;
      selectedIndices.value = [...selectedIndices.value, discardIndex];
    }

    // why: 0 is a legal choice ("up to four"), so submit is enabled at any count ≤ cap.
    const canSubmit = computed<boolean>(() => !isSubmitting.value);

    function onSubmit(): void {
      if (!canSubmit.value || !props.pendingKoDiscardChoice) return;
      isSubmitting.value = true;
      const discard = props.pendingKoDiscardChoice.discard;
      const cardIds = selectedIndices.value.map((discardIndex) => discard[discardIndex]!.cardId);
      props.submitMove("resolveKoDiscardChoice", { cardIds });
    }

    return {
      isSubmitting,
      selectedIndices,
      shouldRender,
      maxCount,
      headingText,
      isSelected,
      toggleCard,
      canSubmit,
      onSubmit,
    };
  },
});
</script>

<template>
  <div
    v-if="shouldRender()"
    class="pending-ko-discard-choice-prompt"
    data-testid="pending-ko-discard-choice-prompt"
    role="region"
    aria-label="KO from discard choice"
  >
    <!-- why: WP-760 / D-24589 — two cards now park this choice (Loki's Maniacal Tyrant,
         Salomé's Fight), so the header is source-neutral; the game log names the source. -->
    <h3 class="pending-ko-discard-choice-prompt__heading">
      {{ headingText }}
    </h3>
    <div class="pending-ko-discard-choice-prompt__cards">
      <button
        v-for="(entry, discardIndex) in pendingKoDiscardChoice!.discard"
        :key="`${discardIndex}:${entry.cardId}`"
        type="button"
        class="pending-ko-discard-choice-prompt__card-btn"
        :class="{ 'pending-ko-discard-choice-prompt__card-btn--selected': isSelected(discardIndex) }"
        :data-testid="`pending-ko-discard-card-${discardIndex}`"
        :disabled="isSubmitting"
        :aria-pressed="isSelected(discardIndex) ? 'true' : 'false'"
        :title="entry.display.name"
        @click="toggleCard(discardIndex)"
      >
        <span class="pending-ko-discard-choice-prompt__card-name">{{ entry.display.name }}</span>
        <img
          v-if="entry.display.imageUrl"
          :src="entry.display.imageUrl"
          :alt="entry.display.name"
          class="pending-ko-discard-choice-prompt__card-image"
        />
      </button>
    </div>
    <button
      type="button"
      class="pending-ko-discard-choice-prompt__submit"
      data-testid="pending-ko-discard-submit"
      :disabled="!canSubmit"
      :aria-disabled="!canSubmit ? 'true' : undefined"
      @click="onSubmit"
    >
      {{ selectedIndices.length === 0 ? 'KO None' : `KO ${selectedIndices.length} card${selectedIndices.length === 1 ? '' : 's'}` }}
    </button>
  </div>
</template>

<style scoped>
.pending-ko-discard-choice-prompt {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 2px solid var(--color-foreground, #333);
  background: var(--color-background, #fff);
}

.pending-ko-discard-choice-prompt__heading {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.pending-ko-discard-choice-prompt__cards {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}

.pending-ko-discard-choice-prompt__card-btn {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.2rem;
  border: 1px solid var(--color-border, #ddd);
  background: var(--color-button-bg, #f5f5f5);
  cursor: pointer;
  align-items: flex-start;
}

.pending-ko-discard-choice-prompt__card-btn--selected {
  border-color: var(--color-foreground, #333);
  background: var(--color-accent, #ffe4b5);
  outline: 2px solid var(--color-foreground, #333);
}

.pending-ko-discard-choice-prompt__card-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pending-ko-discard-choice-prompt__card-name {
  font-size: 0.75rem;
  font-weight: 500;
  max-width: 60px;
}

.pending-ko-discard-choice-prompt__card-image {
  max-width: 60px;
  max-height: 60px;
  object-fit: contain;
}

.pending-ko-discard-choice-prompt__submit {
  align-self: flex-start;
  padding: 0.3rem 0.75rem;
  border: 1px solid var(--color-foreground, #333);
  background: var(--color-button-bg, #f5f5f5);
  cursor: pointer;
  font-weight: 600;
}

.pending-ko-discard-choice-prompt__submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
